import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { updateStatus } from '../utils/updateStatus';
import { TemplateDocumentButton } from '../../../components/TemplateDocumentButton/TemplateDocumentButton';
import { collectionDispatch } from '../utils/collectionDispatch';

/**
 * Component for displaying extra filters in the MailSender show view.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.dispatchAPI - Function for dispatching API requests.
 * @param {Function} props.setRefreshData - Function to set the refresh data state.
 * @param {boolean} props.refreshData - Whether data should be refreshed.
 * @param {Function} props.message - Function for displaying messages.
 * @returns {JSX.Element} React element representing the component.
 */

export const ShowExtraFilters = ({
  dispatchAPI,
  setRefreshData,
  refreshData,
  message,
  mailSender,
  setLoading
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type, id, status, sendingType } = useParams();
  const config = collectionDispatch(type);

  const downloadItems = config.legacy
    ? []
    : (config.multi || [config]).flatMap(({ idsField, collection, useCase }) =>
        (mailSender?.[idsField] || []).map((documentId) => ({
          collection,
          documentId,
          useCase
        }))
      );

  return (
    <Row gutter={16}>
      {status !== 'SENT' && sendingType !== 'EMAIL' ? (
        <Col>
          <Button
            onClick={() =>
              updateStatus({
                dispatchAPI,
                id,
                setRefreshData,
                refreshData,
                message,
                navigate,
                sendingType,
                type,
                mailSender
              })
            }
          >
            {t('mail-sender.buttons.sent')}
          </Button>
        </Col>
      ) : null}
      {sendingType !== 'EMAIL' ? (
        <Col>
          {config?.useCase || config?.multi?.some((m) => m.useCase) ? (
            <TemplateDocumentButton
              parameters={[
                {
                  collection:
                    config.collection ||
                    config?.multi?.find((m) => m.collection)?.collection
                }
              ]}
              use_case={
                config.useCase || config?.multi?.find((m) => m.useCase)?.useCase
              }
              button_text="mail-sender.buttons.dl_template"
              downloadItems={downloadItems}
              type={type}
              id={id}
              setLoading={setLoading}
            />
          ) : null}
          {config.blankUseCase || config?.multi?.some((m) => m.blankUseCase) ? (
            <TemplateDocumentButton
              parameters={[
                {
                  collection:
                    config.blankCollection ||
                    config?.multi?.find((m) => m.blankCollection)
                      ?.blankCollection
                }
              ]}
              use_case={
                config.blankUseCase ||
                config?.multi?.find((m) => m.blankUseCase)?.blankUseCase
              }
              button_text="mail-sender.buttons.dl_neutral_template"
              buttonType="link"
              downloadName={`${t(`mail-sender.tags.${type}`)}_vierge`}
            />
          ) : null}
        </Col>
      ) : null}
      {status !== 'SENT' ? (
        <Col>
          <Button
            onClick={() =>
              navigate(`/mail-sender/edit/${type}/${id}/${sendingType}`)
            }
          >
            {t('buttons.edit')}
          </Button>
        </Col>
      ) : null}
    </Row>
  );
};

ShowExtraFilters.propTypes = {
  dispatchAPI: PropTypes.func.isRequired,
  setRefreshData: PropTypes.func.isRequired,
  refreshData: PropTypes.bool.isRequired,
  message: PropTypes.func.isRequired,
  mailSender: PropTypes.shape({}).isRequired,
  setLoading: PropTypes.func
};

ShowExtraFilters.defaultProps = {
  setLoading: () => {}
};
